/**
 * EL PRESUPUESTO POR PASO (MADERINCO-REDISENO-T1, D-126 = a; contrato de la API 529 cbe384c).
 *
 * Cada paso (crear borrador, agregar o quitar seccion, agregar, cambiar o quitar item, parametros) devuelve el
 * presupuesto RELEIDO con su resumen y su revision. El siguiente paso manda esa revision en If-Match; si otro escribio
 * antes, la API contesta 409 VERSION_CAMBIO. clientKey hace que repetir el mismo paso no duplique.
 *
 * REGLAS (las cuida scripts/check-presupuesto-por-paso.mjs):
 *   - Los cuerpos van en camelCase y NUNCA llevan subtotal ni totales: los calcula la base.
 *   - La unidad de un item sin producto es de la lista cerrada (und, m, ml, m2, m3, gnral).
 *   - Sin revision vista no hay paso (salvo crear el borrador).
 *
 * Sin DOM.
 * @module application/presupuestos/porPaso
 */

export const UNIDADES = ['und', 'm', 'ml', 'm2', 'm3', 'gnral'];

const txt = (v) => (v === null || v === undefined ? '' : String(v).trim());
const num = (v) => (v === null || v === undefined || v === '' || !Number.isFinite(Number(v)) ? null : Number(v));
const sinVacios = (o) => Object.fromEntries(Object.entries(o).filter(([, v]) => v !== null && v !== undefined && v !== ''));

/** Una clave por paso: repetir el MISMO paso (reintento) manda la misma; uno nuevo, otra. */
export function claveDePaso(prefijo = 'p') {
  const r = (globalThis.crypto && globalThis.crypto.randomUUID) ? globalThis.crypto.randomUUID() : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  return `${prefijo}-${r}`;
}

export function cuerpoBorrador({ codigo, proyecto, clienteNombre, ciudad, utilidadPct, ivaUtilidadPct }, clientKey) {
  const c = txt(codigo);
  if (!c) return null;
  return sinVacios({ codigo: c, proyecto: txt(proyecto), clienteNombre: txt(clienteNombre), ciudad: txt(ciudad),
    utilidadPct: num(utilidadPct), ivaUtilidadPct: num(ivaUtilidadPct), clientKey });
}

export function cuerpoSeccion({ nombre, esAdicional, orden }, clientKey) {
  const n = txt(nombre);
  if (!n) return null;
  return sinVacios({ nombre: n, esAdicional: !!esAdicional, orden: num(orden), clientKey });
}

/** Con producto: precio y unidad salen del catalogo salvo que se teclee el precio. Sin producto: unidad de la lista. */
export function cuerpoItem({ seccionId, productoId, titulo, descripcion, unidad, cantidad, precioUnit }, clientKey) {
  const s = txt(seccionId);
  const cant = num(cantidad);
  if (!s || cant === null || cant < 0) return null;
  const conProducto = !!txt(productoId);
  const u = txt(unidad).toLowerCase();
  if (!conProducto && !UNIDADES.includes(u)) return null;
  return sinVacios({ seccionId: s, productoId: txt(productoId), titulo: txt(titulo), descripcion: txt(descripcion),
    unidad: conProducto ? '' : u, cantidad: cant, precioUnit: num(precioUnit), clientKey });
}

/** Solo lo que cambio; sin nada que cambiar, null (no se manda el paso). */
export function cuerpoCambioItem(cambios) {
  const c = sinVacios({ cantidad: num(cambios?.cantidad), precioUnit: num(cambios?.precioUnit), titulo: txt(cambios?.titulo),
    descripcion: txt(cambios?.descripcion), unidad: txt(cambios?.unidad).toLowerCase() });
  if (c.unidad && !UNIDADES.includes(c.unidad)) return null;
  return Object.keys(c).length ? c : null;
}

/** La revision del presupuesto releido (Data.revision). null si no viene: el siguiente paso no se puede mandar. */
export function revisionDe(presupuesto) {
  const r = presupuesto && (presupuesto.revision ?? presupuesto.Revision);
  return Number.isInteger(Number(r)) && r !== null && r !== '' ? Number(r) : null;
}

/** Lo que se le dice al usuario cuando un paso no entra, por causa. */
export function fraseDelFallo(f) {
  if (!f) return '';
  if (f.motivo === 'OTRO_CAMBIO') return 'Alguien mas cambio este presupuesto mientras lo editabas. Lo recargamos: revisa y vuelve a hacer el cambio.';
  if (f.motivo === 'NO_HABILITADO') return 'El armador paso a paso solo esta abierto para el negocio demo.';
  if (f.motivo === 'NO_PUBLICADO') return 'La API todavia no tiene el presupuesto paso a paso.';
  if (f.motivo === 'PROHIBIDO') return f.detalle || 'Tu usuario no tiene acceso a este negocio.';
  if (f.motivo === 'SIN_REVISION') return 'No sabemos que version del presupuesto estas viendo. Recarga antes de seguir.';
  if (f.motivo === 'INVALIDO') return f.detalle || 'Revisa los datos del paso.';
  return f.detalle || 'No pudimos guardar el paso.';
}

const seccionesDe = (p) => (p && (p.secciones ?? p.Secciones)) || [];
const idDe = (x) => String((x && (x.id ?? x.Id)) || '');
const nombreDe = (x) => String((x && (x.nombre ?? x.Nombre)) || '').trim();

/**
 * GUARDAR POR PASOS: corre en orden borrador -> secciones -> items -> parametros, llevando la revision de cada
 * respuesta al siguiente paso. Las claves de idempotencia salen de `base` + el numero del paso: si el usuario vuelve
 * a tocar Guardar despues de un corte, los pasos que ya entraron NO se duplican.
 *
 * Devuelve:
 *   { ok: true, presupuesto, pasos }                         todo entro;
 *   { ok: false, motivo: 'NO_HABILITADO' }                   este negocio no usa pasos: el armador guarda como siempre;
 *   { ok: false, motivo, detalle, parcial: {id, pasos} }     se corto a mitad: el borrador existe con `pasos` hechos.
 *
 * @param {object} cliente  PresupuestoPorPasoApiClient (o uno simulado)
 * @param {object} armador  { codigo, proyecto, cliente, utilidadPct, ivaUtilidadPct, descuento, areaM2, secciones[] }
 * @param {string} base     clave de este Guardar (la misma en el reintento)
 */
export async function guardarPorPasos(cliente, armador, base) {
  let n = 0;
  const clave = () => `${base}-${++n}`;
  const b = await cliente.crearBorrador(cuerpoBorrador({ codigo: armador.codigo, proyecto: armador.proyecto, clienteNombre: armador.cliente,
    utilidadPct: armador.utilidadPct, ivaUtilidadPct: armador.ivaUtilidadPct }, clave()));
  if (!b.ok) return b.motivo === 'NO_HABILITADO' ? { ok: false, motivo: 'NO_HABILITADO' } : { ...b, parcial: null };
  let actual = b.data;
  let rev = b.revision;
  const id = idDe(actual);
  let pasos = 1;
  const corte = (r) => ({ ok: false, motivo: r.motivo, detalle: r.detalle, parcial: { id, pasos } });
  for (const s of armador.secciones || []) {
    const antes = new Set(seccionesDe(actual).map(idDe));
    const rs = await cliente.agregarSeccion(id, rev, cuerpoSeccion({ nombre: s.nombre, esAdicional: s.esAdicional }, clave()));
    if (!rs.ok) return corte(rs);
    actual = rs.data; rev = rs.revision; pasos++;
    // La seccion nueva es la que no estaba antes (con el mismo nombre si hubiera varias nuevas).
    const nueva = seccionesDe(actual).find((x) => !antes.has(idDe(x)) && nombreDe(x) === String(s.nombre || '').trim())
      || seccionesDe(actual).find((x) => !antes.has(idDe(x)));
    if (!nueva) return corte({ motivo: 'ERROR_API', detalle: 'la API no devolvio la seccion recien creada' });
    for (const it of s.items || []) {
      const cuerpo = cuerpoItem({ seccionId: idDe(nueva), productoId: it.productoId, titulo: it.titulo, descripcion: it.descripcion, unidad: it.unidad,
        cantidad: it.cantidad, precioUnit: it.precioUnit }, clave());
      if (!cuerpo) return corte({ motivo: 'INVALIDO', detalle: `el item "${it.titulo || ''}" no tiene cantidad o unidad valida` });
      const ri = await cliente.agregarItem(id, rev, cuerpo);
      if (!ri.ok) return corte(ri);
      actual = ri.data; rev = ri.revision; pasos++;
    }
  }
  const rp = await cliente.parametros(id, rev, { descuento: Number(armador.descuento) || 0, areaM2: Number(armador.areaM2) || undefined });
  if (!rp.ok) return corte(rp);
  return { ok: true, presupuesto: rp.data, pasos: pasos + 1 };
}

const itemsDe = (s) => (s && (s.items ?? s.Items)) || [];
const ordenadas = (p) => [...seccionesDe(p)].sort((a, b) => {
  const ad = !!(a.esAdicional ?? a.EsAdicional), bd = !!(b.esAdicional ?? b.EsAdicional);
  return ad === bd ? (Number(a.orden ?? a.Orden ?? 0) - Number(b.orden ?? b.Orden ?? 0)) : (ad ? 1 : -1);
});

/**
 * Despues del primer Guardar por pasos: a cada seccion e item del armador le pone el id que quedo en el servidor
 * (`_id`), en el mismo orden en que se crearon. Con eso los siguientes Guardar saben que cambiar y que quitar.
 * Devuelve false si las cuentas no casan (entonces no se enlaza nada: el siguiente Guardar no adivina).
 */
export function enlazarIds(armador, presupuesto) {
  const srv = ordenadas(presupuesto);
  const loc = armador.secciones || [];
  if (srv.length !== loc.length || srv.some((s, i) => itemsDe(s).length !== (loc[i].items || []).length)) return false;
  loc.forEach((s, i) => {
    s._id = idDe(srv[i]);
    (s.items || []).forEach((it, j) => { it._id = idDe(itemsDe(srv[i])[j]); });
  });
  return true;
}

const igual = (a, b) => Math.abs((Number(a) || 0) - (Number(b) || 0)) < 1e-9;

/**
 * Los pasos para llevar el borrador del servidor a lo que hay en el armador (Guardar otra vez = el MISMO borrador,
 * A #27980). Orden: quitar items, quitar secciones, cambiar items, agregar secciones (con sus items), agregar items.
 * Solo se manda lo que cambio; si nada cambio, la lista sale vacia.
 * @returns {Array<{tipo:string, ...}>}
 */
export function planDeSincronizacion(presupuesto, armador) {
  const srvSecs = seccionesDe(presupuesto);
  const srvItem = new Map();
  srvSecs.forEach((s) => itemsDe(s).forEach((it) => srvItem.set(idDe(it), { ...it, _sec: idDe(s) })));
  const locSecIds = new Set((armador.secciones || []).map((s) => s._id).filter(Boolean));
  const locItemIds = new Set((armador.secciones || []).flatMap((s) => (s.items || []).map((it) => it._id)).filter(Boolean));
  const pasos = [];
  for (const [id, it] of srvItem) if (!locItemIds.has(id) && locSecIds.has(it._sec)) pasos.push({ tipo: 'quitarItem', itemId: id });
  for (const s of srvSecs) if (!locSecIds.has(idDe(s))) pasos.push({ tipo: 'quitarSeccion', seccionId: idDe(s) });
  for (const s of armador.secciones || []) {
    if (!s._id) { pasos.push({ tipo: 'agregarSeccion', seccion: s }); continue; }
    for (const it of s.items || []) {
      if (!it._id) { pasos.push({ tipo: 'agregarItem', seccionId: s._id, item: it }); continue; }
      const v = srvItem.get(it._id);
      if (!v) { pasos.push({ tipo: 'agregarItem', seccionId: s._id, item: it }); continue; }
      const cambio = cuerpoCambioItem({
        cantidad: igual(v.cantidad ?? v.Cantidad, it.cantidad) ? null : it.cantidad,
        precioUnit: igual(v.precioUnit ?? v.PrecioUnit, it.precioUnit) ? null : it.precioUnit,
        titulo: String(v.titulo ?? v.Titulo ?? '') === String(it.titulo ?? '') ? null : it.titulo,
        descripcion: String(v.descripcion ?? v.Descripcion ?? '') === String(it.descripcion ?? '') ? null : it.descripcion });
      if (cambio) pasos.push({ tipo: 'cambiarItem', itemId: it._id, cambio });
    }
  }
  return pasos;
}

/**
 * Aplica un plan de planDeSincronizacion sobre el MISMO borrador (Ares #28032: al Guardar), llevando la revision.
 * Una seccion nueva lleva sus items detras (con el id de la seccion que devuelve la API). Al final, los parametros.
 * Mismas claves deterministas que guardarPorPasos (`base` + numero): repetir no duplica.
 * @returns {{ok:true, presupuesto, pasos}|{ok:false, motivo, detalle, parcial:{id, pasos}}}
 */
export async function aplicarPlan(cliente, presupuesto, plan, armador, base) {
  const id = idDe(presupuesto);
  const revision = revisionDe(presupuesto);
  let n = 0;
  const clave = () => `${base}-${++n}`;
  let rev = revision;
  let actual = presupuesto;
  let pasos = 0;
  const corte = (r) => ({ ok: false, motivo: r.motivo, detalle: r.detalle, parcial: { id, pasos } });
  const tomar = (r) => { actual = r.data; rev = r.revision; pasos++; };
  for (const p of plan) {
    let r;
    if (p.tipo === 'quitarItem') r = await cliente.quitarItem(id, rev, p.itemId);
    else if (p.tipo === 'quitarSeccion') r = await cliente.quitarSeccion(id, rev, p.seccionId);
    else if (p.tipo === 'cambiarItem') r = await cliente.cambiarItem(id, rev, p.itemId, p.cambio);
    else if (p.tipo === 'agregarItem') {
      const cuerpo = cuerpoItem({ ...p.item, seccionId: p.seccionId }, clave());
      if (!cuerpo) return corte({ motivo: 'INVALIDO', detalle: `el item "${p.item.titulo || ''}" no tiene cantidad o unidad valida` });
      r = await cliente.agregarItem(id, rev, cuerpo);
    } else if (p.tipo === 'agregarSeccion') {
      const antes = new Set(seccionesDe(actual).map(idDe));
      r = await cliente.agregarSeccion(id, rev, cuerpoSeccion({ nombre: p.seccion.nombre, esAdicional: p.seccion.esAdicional }, clave()));
      if (!r.ok) return corte(r);
      tomar(r);
      const nueva = seccionesDe(actual).find((x) => !antes.has(idDe(x)) && nombreDe(x) === String(p.seccion.nombre || '').trim());
      if (!nueva) return corte({ motivo: 'ERROR_API', detalle: 'la API no devolvio la seccion recien creada' });
      for (const it of p.seccion.items || []) {
        const cuerpo = cuerpoItem({ ...it, seccionId: idDe(nueva) }, clave());
        if (!cuerpo) return corte({ motivo: 'INVALIDO', detalle: `el item "${it.titulo || ''}" no tiene cantidad o unidad valida` });
        const ri = await cliente.agregarItem(id, rev, cuerpo);
        if (!ri.ok) return corte(ri);
        tomar(ri);
      }
      continue;
    } else continue;
    if (!r.ok) return corte(r);
    tomar(r);
  }
  const rp = await cliente.parametros(id, rev, { descuento: Number(armador.descuento) || 0, areaM2: Number(armador.areaM2) || undefined,
    utilidadPct: Number(armador.utilidadPct) || undefined, ivaUtilidadPct: Number(armador.ivaUtilidadPct) || undefined,
    proyecto: armador.proyecto || undefined, clienteNombre: armador.cliente || undefined });
  if (!rp.ok) return corte(rp);
  return { ok: true, presupuesto: rp.data, revision: rp.revision, pasos: pasos + 1 };
}

/**
 * EL GUARDAR DEL ARMADOR (Ares #28032, D-126 = a): en el negocio demo, por pasos sobre el MISMO borrador; en
 * cualquier otro, el guardado de siempre. No adivina el demo: el primer paso contesta PUERTA_CERRADA y desde ahi
 * este armador usa el clasico (armador._porPaso = false).
 *   - primera vez: guardarPorPasos (crea el borrador y corre los pasos) y enlaza los ids;
 *   - siguientes: planDeSincronizacion + aplicarPlan (solo lo que cambio) y vuelve a enlazar.
 * La clave del Guardar se conserva mientras no termine bien: tocar Guardar despues de un corte no duplica.
 * @param {{porPaso:object, clasico:Function}} clientes  clasico() = el guardado de siempre (guardarPresupuesto)
 * @returns {Promise<{ok:boolean, modo:'pasos'|'clasico', id?, codigo?, version?, totalServidor?, motivo?, detalle?, parcial?}>}
 */
export async function guardarArmador(clientes, armador) {
  if (armador._porPaso === false) return { ...(await clientes.clasico()), modo: 'clasico' };
  armador._claveGuardar = armador._claveGuardar || claveDePaso('g');
  const r = armador._servidor
    ? await aplicarPlan(clientes.porPaso, armador._servidor, planDeSincronizacion(armador._servidor, armador), armador, armador._claveGuardar)
    : await guardarPorPasos(clientes.porPaso, armador, armador._claveGuardar);
  if (!r.ok && r.motivo === 'NO_HABILITADO') {
    armador._porPaso = false;
    return { ...(await clientes.clasico()), modo: 'clasico' };
  }
  if (!r.ok) return { ok: false, modo: 'pasos', motivo: r.motivo, detalle: r.detalle, parcial: r.parcial };
  const p = r.presupuesto || {};
  armador._servidor = p;
  armador._claveGuardar = null;
  // Si las cuentas no casan, enlazarIds no pone ids: el siguiente plan quita lo del servidor y lo vuelve a agregar
  // (converge sobre el MISMO borrador). Dejar _servidor vacio crearia OTRO borrador al guardar otra vez.
  enlazarIds(armador, p);
  const res = p.resumen ?? p.Resumen ?? {};
  return { ok: true, modo: 'pasos', id: idDe(p), codigo: String(p.codigo ?? p.Codigo ?? ''), version: Number(p.version ?? p.Version ?? 0) || null,
    totalServidor: Number(res.total ?? res.Total) };
}
